import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $initialCurrenciesAvailableStore, $yearlyTransactionStore } from '@lib/store';
import { cn, DateUtils, PaletteUtils } from '@shared/utils';
import type { PaymentSummariesResponse } from '@shared/api/rest/dashboards';
import { DurationType } from '@shared/api/models';
import { TransactionDirection } from '@shared/api';
import { TransactionItem } from './transactionItem';
// import { RequestTransaction } from './requestTransaction';
import { Select, Spinner, Tabs, Text } from '@shared/widgets';

interface YearlyTransactionProps {
  minimalYear: number;
}

export const YearlyTransaction = (props: YearlyTransactionProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const payment = useUnit($yearlyTransactionStore.store);

  const yearsList = DateUtils.getYearsList(props.minimalYear);
  const [selectedYear, setSelectedYear] = useState<string | undefined>(yearsList[yearsList.length - 1]);
  const [currency, setCurrency] = useState<string | undefined>(currenciesAvailableStore.data[0]);
  const [tab, setTab] = useState<number>(1);

  const { t } = useTranslation('dashboard');

  useEffect(() => {
    if (currency) {
      $yearlyTransactionStore.events.clear();
      $yearlyTransactionStore.events.update({
        Currency: currency!,
        DateFrom: DateUtils.getFirstDateOfYear(selectedYear, '01'),
        DateTo: DateUtils.getLastDateOfYear(selectedYear, '12'),
      });
    }
  }, [currency, selectedYear]);

  const getTransactionItem = () => {
    if (payment.loading) return <Spinner className={cn(PaletteUtils.isVcfcPalette && 'py-10')} />;

    switch (tab) {
      case 1:
        return (
          <TransactionItem
            {...(payment.data as PaymentSummariesResponse).outgoingTransactions}
            textValue={selectedYear!}
            currency={currency!}
            itemType={TransactionDirection.Outgoing}
            type={DurationType.Yearly}
          />
        );
      case 2:
        return (
          <TransactionItem
            {...(payment.data as PaymentSummariesResponse).incomingTransactions}
            textValue={selectedYear!}
            currency={currency!}
            itemType={TransactionDirection.Incoming}
            type={DurationType.Yearly}
          />
        );
      // case 3:
      //   return (
      //     <RequestTransaction
      //       {...(payment.data as PaymentSummariesResponse).outgoingTransactionSummary}
      //       textValue={selectedYear!}
      //       currency={currency!}
      //     />
      //   );
      default:
        return null;
    }
  };
  const tabs = useMemo(
    () => [
      { label: t('payment.tabs.outgoingTransactions'), value: 1 },
      { label: t('payment.tabs.incomingTransactions'), value: 2 },
      // { label: t('payment.tabs.requestOutgoingSummary'), value: 3 },
    ],
    [t],
  );
  return (
    <div className={cn(PaletteUtils.isVcfcPalette && 'rounded-xl bg-white dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black')}>
      <div className={cn('flex items-center justify-between', PaletteUtils.isVcfcPalette && ['p-6 pb-0', 'max-md:flex-col max-md:items-start'])}>
        <Text className='mr-16' weight={PaletteUtils.isVcfcPalette ? 'semibold' : undefined} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
          {t('payment.yearlyTransaction.title')}
        </Text>
        <div className={cn('flex gap-6', PaletteUtils.isVcfcPalette && 'max-md:mt-3 max-md:w-full max-md:flex-col')}>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={setCurrency} />
          <Select items={yearsList} value={selectedYear} onChange={setSelectedYear} />
        </div>
      </div>
      <div
        className={cn(
          'relative flex',
          PaletteUtils.isInsigniaPalette && 'my-6',
          PaletteUtils.isVcfcPalette && ['pl-2', 'max-md:no-scrollbar max-md:overflow-x-auto max-md:pb-4'],
        )}
      >
        <Tabs className='w-full p-4' options={tabs} value={tab} onChange={setTab} />
      </div>
      {getTransactionItem()}
    </div>
  );
};
